/**
 * AutoletturaPage v2 — invio letture contatore.
 *
 * Replica del mobile `Autolettura.js`:
 *  1. Back link + header titolo
 *  2. Header inline con "Come si fa?" + indirizzo POD
 *  3. Card promemoria scadenza (primo del mese prossimo)
 *  4. Card con 3 KwhInput (F1/F2/F3) + selettore data (max oggi)
 *  5. CTA "Continua" → dialog di conferma → mutation sendAutoLettura
 *  6. Stato success: card verde + auto-redirect alla Home dopo 2s
 *
 * Logica preservata 1:1:
 *  - Tutti e 3 i campi obbligatori (backend GraphQL Float!)
 *  - Date format AWSDate `YYYY-MM-DD`
 *  - Validazione: cifre + virgola, max 3 decimali (KwhInput)
 */

import { useEffect, useRef, useState, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import dayjs from 'dayjs'
import {
  AlertCircle,
  AlarmClock,
  Calendar,
  CheckCircle2,
  ChevronLeft,
  HelpCircle,
  MapPin,
  Send,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Spinner } from '@/components/ui/spinner'
import { useActiveContract } from '@/features/auth/hooks/useActiveContract'
import { getPodLabelParts } from '@/features/contracts/podLabel'
import { useSendAutolettura } from '@/lib/graphql/hooks'
import { AutoletturaConfirmDialog } from './components/ConfirmDialog'
import { InstructionsDialog } from './components/InstructionsDialog'
import { KwhInput } from './components/KwhInput'

const AUTO_BACK_MS = 2000

function parseKwh(raw: string): number | null {
  if (raw === '' || raw == null) return null
  if (/,$/.test(raw)) return null // virgola finale senza decimali
  const num = Number(raw.replace(',', '.'))
  if (!Number.isFinite(num) || num < 0) return null
  return num
}

function validate(raw: string, name: string): string | null {
  if (raw === '' || raw == null) return `Inserisci la lettura ${name}.`
  if (/,$/.test(raw)) return 'Inserisci le cifre decimali dopo la virgola.'
  const num = Number(raw.replace(',', '.'))
  if (!Number.isFinite(num)) return 'Valore non valido.'
  if (num < 0) return 'Il valore deve essere positivo.'
  return null
}

export function AutoletturaPage() {
  const navigate = useNavigate()
  const { cliente, activeContract, isLoading } = useActiveContract()
  const sendMutation = useSendAutolettura()

  const f2Ref = useRef<HTMLInputElement>(null)
  const f3Ref = useRef<HTMLInputElement>(null)

  const [F1, setF1] = useState('')
  const [F2, setF2] = useState('')
  const [F3, setF3] = useState('')
  const [errF1, setErrF1] = useState<string | null>(null)
  const [errF2, setErrF2] = useState<string | null>(null)
  const [errF3, setErrF3] = useState<string | null>(null)

  const [date, setDate] = useState(() => dayjs().format('YYYY-MM-DD'))
  const [showInstructions, setShowInstructions] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [sendError, setSendError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  const today = dayjs().format('YYYY-MM-DD')
  const expDate = dayjs().add(1, 'month').startOf('month').add(1, 'day').format('DD/MM/YYYY')
  const dateDisplay = dayjs(date).format('DD/MM/YYYY')

  const podParts = getPodLabelParts(activeContract?.pod)

  // Auto-redirect a Home dopo successo
  useEffect(() => {
    if (!success) return
    const t = setTimeout(() => navigate('/', { replace: true }), AUTO_BACK_MS)
    return () => clearTimeout(t)
  }, [success, navigate])

  function onContinua(e: FormEvent) {
    e.preventDefault()
    const e1 = validate(F1, 'F1')
    const e2 = validate(F2, 'F2')
    const e3 = validate(F3, 'F3')
    setErrF1(e1)
    setErrF2(e2)
    setErrF3(e3)
    if (e1 || e2 || e3) return
    setSendError(null)
    setShowConfirm(true)
  }

  async function onConfirm() {
    if (!cliente || !activeContract) return
    const n1 = parseKwh(F1)
    const n2 = parseKwh(F2)
    const n3 = parseKwh(F3)
    if (n1 == null || n2 == null || n3 == null) return

    try {
      await sendMutation.mutateAsync({
        codice_cliente: cliente.codice_cliente,
        codice_contratto: activeContract.codice_contratto,
        data_misura: date,
        F1: n1,
        F2: n2,
        F3: n3,
      })
      setShowConfirm(false)
      setSuccess(true)
    } catch (err) {
      console.warn('[Autolettura] send failed:', err)
      setSendError(
        err instanceof Error
          ? err.message
          : 'Errore di rete. Verifica la connessione e riprova.',
      )
      setShowConfirm(false)
    }
  }

  // --- Stato success --------------------------------------------------

  if (success) {
    return (
      <div className="mx-auto flex max-w-xl flex-col gap-4">
        <Link
          to="/"
          className="inline-flex w-fit items-center gap-1 text-sm font-semibold text-secondary hover:underline"
        >
          <ChevronLeft className="h-4 w-4" />
          Home
        </Link>
        <Card className="flex flex-col items-center gap-3 p-8 text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-success/15 text-success">
            <CheckCircle2 className="h-7 w-7" />
          </div>
          <h1 className="text-2xl font-bold text-foreground">Autolettura inviata</h1>
          <p className="max-w-sm text-sm text-muted-foreground">
            Le tue letture sono state ricevute correttamente.
          </p>
        </Card>
      </div>
    )
  }

  // --- Stato loading contratti ----------------------------------------

  if (isLoading || !activeContract || !cliente) {
    return (
      <div className="mx-auto flex max-w-xl flex-col gap-4">
        <Link
          to="/"
          className="inline-flex w-fit items-center gap-1 text-sm font-semibold text-secondary hover:underline"
        >
          <ChevronLeft className="h-4 w-4" />
          Home
        </Link>
        <Card className="flex items-center justify-center gap-3 p-8 text-sm text-muted-foreground">
          <Spinner />
          Caricamento dati contratto…
        </Card>
      </div>
    )
  }

  // --- Form -----------------------------------------------------------

  return (
    <div className="mx-auto flex max-w-xl flex-col gap-4">
      <Link
        to="/"
        className="inline-flex w-fit items-center gap-1 text-sm font-semibold text-secondary hover:underline"
      >
        <ChevronLeft className="h-4 w-4" />
        Home
      </Link>

      {/* Header */}
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex-1">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            <h1 className="text-2xl font-bold text-foreground md:text-3xl">
              Inserisci le letture
            </h1>
            {podParts && (
              <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1 text-sm">
                <MapPin className="h-3.5 w-3.5 shrink-0 text-secondary" />
                {podParts.display && (
                  <span className="font-semibold text-foreground">{podParts.display}</span>
                )}
                <span
                  className={
                    podParts.display
                      ? 'font-mono text-xs text-muted-foreground'
                      : 'font-mono text-xs font-semibold text-foreground'
                  }
                >
                  {podParts.code}
                </span>
              </span>
            )}
          </div>
          <p className="mt-1.5 text-sm text-muted-foreground">
            Compila i 3 campi e seleziona la data di rilevazione.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowInstructions(true)}
          className="inline-flex items-center gap-1 text-sm font-semibold text-secondary underline-offset-2 hover:underline"
        >
          <HelpCircle className="h-4 w-4" />
          Come si fa?
        </button>
      </header>

      {/* Promemoria scadenza */}
      <Card className="flex items-center gap-3 border-warning/40 bg-warning/5 p-4">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-warning/20">
          <AlarmClock className="h-5 w-5 text-warning-foreground" />
        </div>
        <div className="flex flex-col">
          <p className="text-sm font-semibold text-foreground">Promemoria</p>
          <p className="text-xs text-muted-foreground">Invia entro il {expDate}</p>
        </div>
      </Card>

      {/* Form letture */}
      <form onSubmit={onContinua}>
        <Card className="flex flex-col gap-4 p-5">
          <KwhInput
            label="Fascia F1 (A1+)"
            value={F1}
            onChange={(v) => {
              setF1(v)
              if (errF1) setErrF1(null)
            }}
            error={errF1}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                f2Ref.current?.focus()
              }
            }}
            autoFocus
          />
          <KwhInput
            ref={f2Ref}
            label="Fascia F2 (A2+)"
            value={F2}
            onChange={(v) => {
              setF2(v)
              if (errF2) setErrF2(null)
            }}
            error={errF2}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                f3Ref.current?.focus()
              }
            }}
          />
          <KwhInput
            ref={f3Ref}
            label="Fascia F3 (A3+)"
            value={F3}
            onChange={(v) => {
              setF3(v)
              if (errF3) setErrF3(null)
            }}
            error={errF3}
          />

          {/* Data rilevazione */}
          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="auto-date"
              className="text-sm font-semibold text-foreground"
            >
              Data di rilevazione
            </label>
            <div className="flex h-12 items-center rounded-xl border-[1.5px] border-input bg-background px-4 focus-within:border-secondary">
              <Calendar className="h-4 w-4 text-secondary" />
              <input
                id="auto-date"
                type="date"
                value={date}
                max={today}
                onChange={(e) => setDate(e.target.value)}
                /* `color-scheme: light dark` dice al browser di adattare i
                 * controlli nativi (incluso il bottone calendario) al tema
                 * attivo. Senza, Chromium renderizza un'icona scura non
                 * visibile in dark mode. */
                style={{ colorScheme: 'light dark' }}
                className="ml-3 flex-1 bg-transparent text-[15px] outline-none"
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Oggi: <span className="font-medium text-foreground">{dateDisplay}</span>
            </p>
          </div>

          {sendError && (
            <div className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
              <div className="flex flex-col">
                <p className="font-semibold text-destructive">Invio non riuscito</p>
                <p className="text-xs text-foreground">{sendError}</p>
              </div>
            </div>
          )}

          <Button type="submit" className="mt-2 gap-2">
            <Send className="h-4 w-4" />
            Continua
          </Button>
        </Card>
      </form>

      {/* Dialog conferma */}
      <AutoletturaConfirmDialog
        open={showConfirm}
        onOpenChange={setShowConfirm}
        F1={F1}
        F2={F2}
        F3={F3}
        data={dateDisplay}
        submitting={sendMutation.isPending}
        onConfirm={onConfirm}
      />

      {/* Dialog istruzioni */}
      <InstructionsDialog open={showInstructions} onOpenChange={setShowInstructions} />
    </div>
  )
}

